import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Region, DiscountApprovalStatus, FulfillmentType } from '@kalachand/shared';
import { effectiveDiscountPct, DEFAULT_SALES_DISCOUNT_CAP_PCT } from '@kalachand/shared';
import { apiGet, apiPost, apiPatch, getApiError, api } from '../../lib/api';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Money } from '../../components/Money';
import { StatusPill, orderStatusTone, paymentStatusTone } from '../../components/StatusPill';
import { FirmnessBadge } from '../../components/FirmnessBadge';
import { Button } from '../../components/Button';
import { Can } from '../../lib/permissions';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/cn';
import { businessTodayIso, addDaysIso, formatBusinessDate } from '../../lib/dates';
import { discountPctFromAmount, formatPct } from '../../lib/discount';
import { emptyPayment, type PaymentDraft } from '../../components/PaymentFields';
import { PaymentCapture, paymentsValid } from '../../components/PaymentCapture';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { useToast } from '../../components/Toast';
import { EmailDocButton } from '../../components/EmailDocButton';
import { DeliveryDatePicker } from '../../components/DeliveryDatePicker';
import { RescheduleModal } from '../operations/RescheduleModal';
import { ReturnModal } from './ReturnModal';
import { ChangeSizeModal } from './ChangeSizeModal';

interface OrderDetail {
  id: string;
  orderNo: string;
  number: string;
  // LEGACY = a paper order written before the 1 Jul 2026 go-live. `number` is
  // then the manual invoice number verbatim, and legacyOrderDate is the date it
  // was booked on paper.
  origin?: 'SYSTEM' | 'LEGACY';
  legacyRef?: string | null;
  legacyOrderDate?: string | null;
  status: string;
  fulfillmentType: string;
  paymentStatus: string;
  subtotal: string;
  discountTotal: string;
  vatTotal: string;
  fastTrackFee: string;
  total: string;
  paidTotal: string;
  balanceDue: string;
  deliveryAddress: string | null;
  deliveryAddressPrecise: string | null;
  deliveryDate: string | null;
  pickupShopId: string | null;
  disposalQty: number;
  productionUrgentOverride: boolean;
  region: string | null;
  notes: string | null;
  customer: {
    id: string;
    type: string;
    name: string;
    companyName: string | null;
    contactPerson: string | null;
    vatNumber: string | null;
    brn: string | null;
    phone: string;
    email: string | null;
    region: string | null;
  };
  shop: { id: string; code: string; name: string };
  salesperson: { id: string; name: string };
  // Who performed each lifecycle transition (null until it happens).
  confirmedBy: { id: string; name: string } | null;
  readyBy: { id: string; name: string } | null;
  deliveredBy: { id: string; name: string } | null;
  completedBy: { id: string; name: string } | null;
  cancelledBy: { id: string; name: string } | null;
  lines: Array<{
    id: string;
    product: { id: string; sku: string; name: string; modelId: string | null; density: string | null; category?: { fulfillmentStrategy: string; deliveryRequired?: boolean } };
    productSnapshot: { name?: string; sku?: string } | null;
    qty: number;
    returnedQty: number;
    deliveredQty: number;
    backorder: boolean;
    customWidthCm: number | null;
    customLengthCm: number | null;
    unitPrice: string;
    discount: string;
    lineTotal: string;
    vatRate: string;
    selectedColor: string | null;
    selectedSideColor: string | null;
    selectedFabric: string | null;
    selectedDensity: string | null;
    isGift: boolean;
    productionPlans?: Array<{ scheduledDate: string; qty: number }>;
    fulfillmentTask?: { status: string } | null;
  }>;
  payments: Array<{
    id: string;
    amount: string;
    method: string;
    reference: string | null;
    recordedAt: string;
    voidedAt: string | null;
    verifiedAt: string | null;
    rejectedAt: string | null;
    rejectionReason: string | null;
    recordedBy: { id: string; name: string };
    verifiedBy: { id: string; name: string } | null;
    rejectedBy: { id: string; name: string } | null;
  }>;
  paymentRejectedAt: string | null;
  paymentRejectedReason: string | null;
  paymentOverrideKind: 'PO' | 'OWNER_CREDIT' | null;
  paymentOverrideReason: string | null;
  paymentOverrideAt: string | null;
  paymentOverrideBy: { id: string; name: string } | null;
  invoice: {
    id: string;
    number: string;
    issuedAt: string;
    printedAt: string | null;
    creditNoteApplications?: Array<{ id: string; amount: string; creditNote: { number: string } }>;
  } | null;
  invoices: Array<{
    id: string;
    number: string;
    kind: 'full' | 'deposit' | 'balance';
    issuedAt: string;
    subtotal: string;
    vatTotal: string;
    total: string;
  }>;
  discountApprovals: Array<{
    id: string;
    status: DiscountApprovalStatus;
    requestedPct: string;
    approvedPct: string | null;
    capPct: string;
    requestedDiscountTotal: string;
    reason: string | null;
    decisionNote: string | null;
    createdAt: string;
    decidedAt: string | null;
    requestedBy: { id: string; name: string };
    decidedBy: { id: string; name: string } | null;
  }>;
  reservations?: Array<{
    id: string;
    status: 'PENDING' | 'HELD' | 'ALLOCATED' | 'RELEASED';
    qty: number;
    depositHeldAt: string | null;
    allocatedAt: string | null;
    product: { id: string; sku: string; name: string };
  }>;
  createdAt: string;
  confirmedAt: string | null;
  readyAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
}

// Emails the invoice (or pro forma, before an invoice exists) PDF to the
// customer via Resend (recipient editable). The endpoint picks the document.
function EmailInvoiceButton({
  orderId,
  defaultEmail,
  isProforma = false,
}: {
  orderId: string;
  defaultEmail: string | null;
  isProforma?: boolean;
}): JSX.Element {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(defaultEmail ?? '');
  const docLabel = isProforma ? 'pro forma' : 'invoice';
  const send = useMutation({
    mutationFn: () =>
      apiPost<{ status: string; stubbed: boolean; to: string; invoiceNumber: string }>(
        `/orders/${orderId}/invoice/email`,
        { to: to.trim() || undefined },
      ),
    onSuccess: (r) => {
      setOpen(false);
      toast.success(
        r.stubbed
          ? `Email logged (no RESEND_API_KEY set) — would send to ${r.to}`
          : `${isProforma ? 'Pro forma' : 'Invoice'} sent to ${r.to}`,
      );
    },
  });
  return (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          send.reset();
          setTo(defaultEmail ?? '');
          setOpen(true);
        }}
      >
        Email
      </Button>
      {open && (
        <Modal
          title={`Email ${docLabel}`}
          subtitle={`Send the ${docLabel} PDF to the customer.`}
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button loading={send.isPending} disabled={!to.trim()} onClick={() => send.mutate()}>
                Send
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Input
              label="Recipient email"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
            />
            {!defaultEmail && (
              <div className="text-xs text-warning">
                This customer has no email on file — enter one to send.
              </div>
            )}
            {send.isError && (
              <div className="text-xs text-negative">{getApiError(send.error).message}</div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, perms } = useAuth();

  const order = useQuery({
    queryKey: ['order', id],
    queryFn: () => apiGet<OrderDetail>(`/orders/${id}`),
    // Live-update while a discount decision is outstanding: a draft awaiting the
    // owner's sign-off polls so the banner flips to approved/rejected (and the
    // confirm button unlocks) without a manual refresh. Quiet otherwise.
    refetchInterval: (q) => {
      const o = q.state.data;
      return o?.status === 'DRAFT' && o.discountApprovals?.[0]?.status === 'PENDING'
        ? 8_000
        : false;
    },
    refetchOnWindowFocus: true,
  });

  // Discount cap (fraction of gross) — to decide whether a draft is over-cap and
  // therefore blocked from confirming without an owner approval.
  const companyQuery = useQuery({
    queryKey: ['company'],
    queryFn: () => apiGet<{ salesDiscountCapPct: string }>('/company'),
  });

  // Re-request owner approval from the order page (e.g. after a rejection).
  const [reqReason, setReqReason] = useState('');
  const [reqOpen, setReqOpen] = useState(false);
  const requestApproval = useMutation({
    mutationFn: () =>
      apiPost('/discount-approvals', { orderId: id, reason: reqReason.trim() || null }),
    onSuccess: () => {
      setReqOpen(false);
      setReqReason('');
      qc.invalidateQueries({ queryKey: ['order', id] });
    },
  });

  const transition = useMutation({
    mutationFn: (action: 'confirm' | 'ready' | 'delivered' | 'cancel') => {
      if (action === 'cancel')
        return apiPost(`/orders/${id}/cancel`, { reason: 'Cancelled from order detail' });
      return apiPost(`/orders/${id}/${action}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order', id] }),
  });

  // Cancellation — with a reason + a deposit-handling choice (forfeit the
  // non-refundable deposit, or reverse everything incl. the deposit for a
  // company-fault / goodwill refund).
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // 'forfeit' = keep deposit, restock; 'refund' = full refund, restock (goods
  // returned in good condition / goodwill); 'damaged' = full refund, write off
  // (goods damaged, NOT returned to sellable stock).
  const [cancelMode, setCancelMode] = useState<'forfeit' | 'refund' | 'damaged'>('forfeit');
  const cancelMut = useMutation({
    mutationFn: () =>
      apiPost(`/orders/${id}/cancel`, {
        reason: cancelReason,
        reverseDeposit: cancelMode !== 'forfeit',
        restock: cancelMode !== 'damaged',
      }),
    onSuccess: () => {
      setShowCancel(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
    },
  });

  // Discard — for an UNPAID order (a draft, or a confirmed order nobody has paid
  // for). Reuses the cancel endpoint: with no payment there's no invoice to
  // reverse, so it just restocks (if it was confirmed) and marks the order
  // CANCELLED — no deposit/credit-note choices to make.
  const [showDiscard, setShowDiscard] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const discardMut = useMutation({
    mutationFn: () =>
      apiPost(`/orders/${id}/cancel`, {
        reason: discardReason.trim() || 'Discarded — unpaid order',
        restock: true,
      }),
    onSuccess: () => {
      setShowDiscard(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      navigate('/orders');
    },
  });

  // Payment override (owner-only) — PO terms or ad-hoc credit. Bypasses the
  // finance verification gate so production / delivery proceed regardless.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideKind, setOverrideKind] = useState<'PO' | 'OWNER_CREDIT'>('PO');
  const [overrideReason, setOverrideReason] = useState('');
  const applyOverride = useMutation({
    mutationFn: () =>
      apiPost(`/orders/${id}/payment-override`, { kind: overrideKind, reason: overrideReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', id] });
      setOverrideOpen(false);
      setOverrideReason('');
    },
  });
  const clearOverride = useMutation({
    mutationFn: () => api.delete(`/orders/${id}/payment-override`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order', id] }),
  });

  const [returnOpen, setReturnOpen] = useState(false);
  const [changeSizeLine, setChangeSizeLine] = useState<
    { id: string; productId: string; modelId: string; qty: number; currentGross: number; label: string } | null
  >(null);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const handover = useMutation({
    mutationFn: (handoverLines: Array<{ orderLineId: string; qty: number }>) =>
      apiPost(`/orders/${id}/handover`, { lines: handoverLines }),
    onSuccess: () => {
      setHandoverOpen(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  // Set / change fulfilment on a DRAFT (the wizard defers this step for over-cap
  // orders sent for approval; the salesperson completes it here before paying).
  const [editFulfilment, setEditFulfilment] = useState(false);

  // Rescheduling (operations + owner) — delivery date and per-line production day.
  const [editDelivery, setEditDelivery] = useState(false);
  const [prodLine, setProdLine] = useState<{ id: string; label: string; date: string } | null>(
    null,
  );
  const reschedProd = useMutation({
    mutationFn: (vars: {
      orderLineId: string;
      date: string;
      override?: boolean;
      reason?: string;
    }) =>
      apiPatch(`/orders/${id}/production`, {
        orderLineId: vars.orderLineId,
        productionDate: vars.date,
        override: vars.override,
        reason: vars.reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', id] });
      setProdLine(null);
    },
  });

  if (order.isLoading)
    return (
      <PageScaffold title="Order" back={{ to: '/orders', label: 'Orders' }}>
        <div className="text-ink-3">Loading…</div>
      </PageScaffold>
    );
  if (!order.data)
    return (
      <PageScaffold title="Order" back={{ to: '/orders', label: 'Orders' }}>
        <div className="text-ink-3">Order not found.</div>
      </PageScaffold>
    );
  const o = order.data;
  // Once an order has a payment, a sales girl can no longer edit lines or
  // change status — those become owner/manager actions. Recording further
  // payments is still allowed (see the payments.record block below).
  const locked = user?.role === 'sales_girl' && Number(o.paidTotal) > 0;
  // No money in yet. An unpaid order can't be confirmed (the deposit is what
  // confirms it) and can be discarded cleanly — there's no invoice to reverse.
  const isUnpaid = Number(o.paidTotal) <= 0;
  const todayIso = businessTodayIso();

  // ── Discount-approval state ──────────────────────────────────────────────
  // The order's live effective discount vs the company cap. If a draft is over
  // cap, confirming needs either `discounts.apply_above_ceiling` or an APPROVED
  // request that covers the live pct (matches the backend confirm gate).
  const capPct = companyQuery.data
    ? Number(companyQuery.data.salesDiscountCapPct)
    : DEFAULT_SALES_DISCOUNT_CAP_PCT;
  const discountPct = effectiveDiscountPct(
    Number(o.total) + Number(o.discountTotal),
    Number(o.discountTotal),
  );
  const overCap = discountPct > capPct + 1e-6;
  const canSelfApprove = perms.has('discounts.apply_above_ceiling');
  const latestApproval = o.discountApprovals[0] ?? null;
  const approvedPct =
    latestApproval?.status === 'APPROVED' && latestApproval.approvedPct != null
      ? Number(latestApproval.approvedPct)
      : null;
  const coveredByApproval = approvedPct != null && discountPct <= approvedPct + 1e-6;
  // A draft over the cap is blocked from confirming unless the user can self-
  // approve or there's an approval covering the live discount.
  const confirmBlockedByDiscount =
    o.status === 'DRAFT' && overCap && !canSelfApprove && !coveredByApproval;
  const approvalPending = latestApproval?.status === 'PENDING';
  // The wizard defers the fulfilment step entirely for over-cap orders sent for
  // approval, so the draft lands here with the untouched 'pickup' default and no
  // address/date — indistinguishable from a genuine pickup order unless we check
  // for an approval on file. Flag it so staff can't mistake "never set" for "set".
  const fulfilmentPending =
    o.status === 'DRAFT' &&
    latestApproval != null &&
    o.fulfillmentType === 'pickup' &&
    !o.deliveryAddress &&
    !o.deliveryDate;
  const lineProdDate = (l: OrderDetail['lines'][number]): string | null => {
    const p = l.productionPlans;
    return p && p.length > 0 ? p[p.length - 1]!.scheduledDate.slice(0, 10) : null;
  };

  const downloadInvoice = (mode: 'download' | 'inline', invoiceId?: string): void => {
    // Works before an invoice exists too — the endpoint serves the PRO FORMA.
    const fileName =
      (invoiceId ? o.invoices.find((i) => i.id === invoiceId)?.number : o.invoice?.number) ??
      o.number;
    // The Authorization header isn't sent for window.open, so fetch + blob:
    void (async () => {
      try {
        const params: Record<string, string> = { mode };
        if (invoiceId) params.invoiceId = invoiceId;
        const res = await api.get(`/orders/${o.id}/invoice.pdf`, { params, responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        if (mode === 'inline') {
          const w = window.open(blobUrl, '_blank');
          if (w) setTimeout(() => w.print(), 500);
        } else {
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${fileName}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  // The order no. is the page's identity; the pro forma and any VAT invoice(s)
  // are documents that hang off it, shown as secondary references.
  const docRefs: string[] = [];
  if (!o.number.startsWith('DRAFT-')) {
    // A legacy order's `number` is the customer's own paper invoice number, not
    // a pro forma we issued — say so, or staff will look for a document the
    // system never produced.
    docRefs.push(
      o.origin === 'LEGACY' ? `Manual invoice ${o.number}` : `Pro forma ${o.number}`,
    );
  }
  if (o.origin === 'LEGACY' && o.legacyOrderDate) {
    docRefs.push(`Booked ${new Date(o.legacyOrderDate).toLocaleDateString()}`);
  }
  for (const inv of o.invoices ?? []) docRefs.push(`Invoice ${inv.number}`);

  return (
    <PageScaffold
      back={{ to: '/orders', label: 'Orders' }}
      title={o.orderNo}
      subtitle={docRefs.length ? docRefs.join('  ·  ') : undefined}
      contentClassName="space-y-4"
      actions={
        <>
          {/* Deferred-VAT: before an invoice is minted the order prints as a
              PRO FORMA; after handover it's the VAT invoice. The endpoint picks. */}
          {o.status !== 'DRAFT' && (
            <>
              <Button variant="secondary" onClick={() => downloadInvoice('download')}>
                {o.invoice ? 'Download invoice' : 'Pro forma PDF'}
              </Button>
              <Button variant="secondary" onClick={() => downloadInvoice('inline')}>
                {o.invoice ? 'Print' : 'Print pro forma'}
              </Button>
              {/* Email the document — pro forma before an invoice exists, the VAT
                  invoice once it's minted. The endpoint picks which. */}
              {!o.invoice && <EmailInvoiceButton orderId={o.id} defaultEmail={o.customer.email} isProforma />}
            </>
          )}
          {/* Invoice-only actions (need a minted tax invoice). */}
          {o.invoice && (
            <>
              <Button variant="ghost" disabled title="Wired in Phase 2">
                WhatsApp
              </Button>
              <EmailInvoiceButton orderId={o.id} defaultEmail={o.customer.email} />
              {/* Owner-only — process a customer return / exchange (restock + refund CN) */}
              <Can permission="credit_notes.create">
                {o.status !== 'CANCELLED' && o.lines.some((l) => l.qty - l.returnedQty > 0) && (
                  <Button
                    variant="ghost"
                    onClick={() => setReturnOpen(true)}
                    title="Owner only — restock + issue a refund credit note"
                  >
                    ↩ Return / exchange
                  </Button>
                )}
              </Can>
              {/* Owner-only — issues a credit note pre-filled with this order's lines */}
              <Can permission="credit_notes.create">
                {o.status !== 'CANCELLED' && (
                  <Link to={`/credits/new?orderId=${o.id}`}>
                    <Button
                      variant="ghost"
                      title="Owner only — reduce customer AR after invoice issue"
                    >
                      ❎ Issue credit note
                    </Button>
                  </Link>
                )}
              </Can>
            </>
          )}
          {/* Delivery note — operations creates one when goods leave HQ. Shown
              pre-invoice: in the deferred-VAT model the tax invoice mints AT delivery. */}
          <Can permission="delivery_notes.create">
            {(o.status === 'CONFIRMED' || o.status === 'READY') &&
              o.fulfillmentType === 'delivery' && (
                <Link to={`/delivery-notes/new?orderId=${o.id}`}>
                  <Button variant="ghost" title="Create a delivery note for the driver">
                    🚚 Delivery note
                  </Button>
                </Link>
              )}
          </Can>
          {/* In-store handover of part of the order now (mixed fulfilment:
              hand the pillow over here, deliver the mattress later). */}
          <Can permission="payments.record">
            {(o.status === 'CONFIRMED' || o.status === 'READY') &&
              o.lines.some((l) => l.deliveredQty < l.qty) && (
                <Button
                  variant="ghost"
                  onClick={() => setHandoverOpen(true)}
                  title="Hand over items at the counter now — recorded on the pro forma; the tax invoice issues once the whole order is delivered. Handing over everything left on the order requires the balance to be paid in full first."
                >
                  🧾 Hand over in store
                </Button>
              )}
          </Can>
          {/* Owner override of the finance verification gate */}
          <Can permission="payments.override_verification">
            {o.status !== 'CANCELLED' && !o.paymentOverrideKind && (
              <Button
                variant="ghost"
                onClick={() => setOverrideOpen(true)}
                title="Owner only — bypass finance verification (PO / credit)"
              >
                🔓 Override verification
              </Button>
            )}
            {o.paymentOverrideKind && (
              <Button
                variant="ghost"
                onClick={() => clearOverride.mutate()}
                loading={clearOverride.isPending}
                title="Remove override"
              >
                Remove override
              </Button>
            )}
          </Can>
        </>
      }
    >
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill tone={orderStatusTone(o.status)}>{o.status}</StatusPill>
        <StatusPill tone={paymentStatusTone(o.paymentStatus)}>{o.paymentStatus}</StatusPill>
        {o.region && <StatusPill tone="accent">{o.region.replace('_', '-')}</StatusPill>}
        {o.productionUrgentOverride && (
          <span
            title={`Booked despite full delivery day on ${o.deliveryDate?.slice(0, 10) ?? 'the chosen date'}`}
            className="cursor-help"
          >
            <StatusPill tone="warning">⚠ Capacity override</StatusPill>
          </span>
        )}
        <span className="text-xs text-ink-3">{new Date(o.createdAt).toLocaleString()}</span>
      </div>

      {/* Single invoice is already covered by the title + header PDF/Print.
          Only surface this list when an order has been split into several
          invoices (e.g. deposit + balance), where per-invoice PDFs matter. */}
      {o.invoices.length > 1 && (
        <Card bodyClassName="p-0">
          <div className="px-5 pt-4 pb-1 text-xs uppercase tracking-wider text-ink-3">Invoices</div>
          <table className="w-full text-sm">
            <tbody>
              {o.invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-rule-2">
                  <td className="px-5 py-3">
                    <span className="kc-mono text-ink">{inv.number}</span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-ink-3">
                      Tax invoice
                    </span>
                    <div className="text-xs text-ink-3">
                      {new Date(inv.issuedAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Money value={inv.total} />
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadInvoice('download', inv.id)}
                    >
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadInvoice('inline', inv.id)}
                    >
                      Print
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {o.paymentRejectedAt && (
        <Card className="border-negative/40 bg-negative-soft" bodyClassName="p-4">
          <div className="flex items-start gap-3">
            <span className="text-negative text-xl mt-0.5">⚠</span>
            <div className="flex-1">
              <div className="text-sm font-medium text-negative">
                Finance rejected a payment on this order
              </div>
              {o.paymentRejectedReason && (
                <p className="text-sm text-ink-2 mt-1">{o.paymentRejectedReason}</p>
              )}
              <p className="text-xs text-ink-3 mt-2">
                The order is below the deposit threshold so it can't proceed to delivery yet.
                Stocked goods have been returned to warehouse. Chase the customer to re-pay, then
                wait for finance to verify the new payment.
              </p>
            </div>
          </div>
        </Card>
      )}

      {o.paymentOverrideKind && (
        <Card className="border-info/40 bg-info-soft" bodyClassName="p-4">
          <div className="flex items-start gap-3">
            <span className="text-info text-xl mt-0.5">✓</span>
            <div className="flex-1">
              <div className="text-sm font-medium text-info">
                Payment verification overridden —{' '}
                {o.paymentOverrideKind === 'PO' ? 'B2B Purchase Order' : 'Owner credit'}
              </div>
              {o.paymentOverrideReason && (
                <p className="text-sm text-ink-2 mt-1">{o.paymentOverrideReason}</p>
              )}
              <p className="text-xs text-ink-3 mt-2">
                Applied by {o.paymentOverrideBy?.name ?? 'owner'} on{' '}
                {o.paymentOverrideAt &&
                  new Date(o.paymentOverrideAt).toLocaleDateString('en-MU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                . The order proceeds without finance verification of payments.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Backorder reservations — what this order is waiting on, and the "stock
          has landed" ping. Derived from the order's reservations: ALLOCATED means
          the stock was received + auto-assigned (ready to deliver); HELD/PENDING
          means it's still reserved against an incoming container. */}
      {(() => {
        const active = (o.reservations ?? []).filter((r) => r.status !== 'RELEASED');
        if (active.length === 0 || ['DELIVERED', 'COMPLETED', 'CANCELLED'].includes(o.status)) return null;
        const ready = active.filter((r) => r.status === 'ALLOCATED');
        const waiting = active.filter((r) => r.status === 'HELD' || r.status === 'PENDING');
        const allReady = ready.length > 0 && waiting.length === 0;
        return (
          <Card
            className={allReady ? 'border-positive/40 bg-positive-soft' : 'border-info/40 bg-info-soft'}
            bodyClassName="p-4"
          >
            <div className="flex items-start gap-3">
              <span className={cn('text-xl mt-0.5', allReady ? 'text-positive' : 'text-info')}>📦</span>
              <div className="flex-1">
                {(() => {
                  const collect = o.fulfillmentType === 'pickup';
                  const fulfilWord = collect ? 'collection' : 'delivery';
                  return (
                    <>
                      <div className={cn('text-sm font-medium', allReady ? 'text-positive' : 'text-info')}>
                        {allReady
                          ? `Stock has arrived — this order is ready for ${fulfilWord}`
                          : ready.length > 0
                            ? 'Some stock has arrived — still waiting on the rest'
                            : collect
                              ? 'Reserved — waiting for HQ to transfer the stock to this shop'
                              : 'Reserved — waiting for incoming stock'}
                      </div>
                      <p className="text-sm text-ink-2 mt-1">
                        {active.map((r) => `${r.qty}× ${r.product.name}`).join(', ')}
                      </p>
                      <p className="text-xs text-ink-3 mt-2">
                        {allReady
                          ? collect
                            ? 'The stock arrived at the shop and was allocated to this order. The customer can collect it now.'
                            : 'The backordered stock was received and automatically allocated to this order. Schedule or dispatch the delivery.'
                          : collect
                            ? 'Out at this shop — the warehouse is transferring it in. The order is ready for collection automatically once the shop receives it.'
                            : 'Backordered against incoming warehouse stock. Delivery is scheduled automatically the moment the stock is received — no action needed until then.'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Partial handover — some items given (e.g. the pillow in store), the rest
          still to be delivered. The order stays a pro forma; the VAT invoice mints
          only when the whole order is delivered. */}
      {(() => {
        const handedOver = o.lines.filter((l) => !l.isGift && l.deliveredQty > 0);
        const pendingPriced = o.lines.some((l) => !l.isGift && l.deliveredQty < l.qty);
        if (o.invoice || handedOver.length === 0 || !pendingPriced || o.status === 'CANCELLED') return null;
        return (
          <Card className="border-info/40 bg-info-soft" bodyClassName="p-4">
            <div className="flex items-start gap-3">
              <span className="text-info text-xl mt-0.5">📦</span>
              <div className="flex-1">
                <div className="text-sm font-medium text-info">Partially handed over — still a pro forma</div>
                <p className="text-sm text-ink-2 mt-1">
                  Already handed over: {handedOver.map((l) => `${l.deliveredQty}× ${l.product.name}`).join(', ')}.
                </p>
                <p className="text-xs text-ink-3 mt-2">
                  The VAT (tax) invoice is issued only once the whole order is delivered. Until then this stays a pro forma.
                </p>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Discount approval — only while the order is a draft (the window where
          confirming is gated on the discount cap). */}
      {o.status === 'DRAFT' && ((overCap && !canSelfApprove) || approvalPending) && (
        <Card
          className={cn(
            approvalPending
              ? 'border-warning/40 bg-warning-soft'
              : latestApproval?.status === 'APPROVED'
                ? 'border-positive/40 bg-positive-soft'
                : latestApproval?.status === 'REJECTED'
                  ? 'border-negative/40 bg-negative-soft'
                  : 'border-warning/40 bg-warning-soft',
          )}
          bodyClassName="p-4"
        >
          <div className="flex items-start gap-3">
            <span className="text-xl mt-0.5">
              {approvalPending ? '⏳' : latestApproval?.status === 'APPROVED' ? '✓' : '⚠'}
            </span>
            <div className="flex-1 min-w-0">
              {approvalPending ? (
                <>
                  <div className="text-sm font-medium text-warning">Discount approval pending</div>
                  <p className="text-sm text-ink-2 mt-1">
                    {latestApproval!.requestedBy.name} requested{' '}
                    {formatPct(Number(latestApproval!.requestedPct) * 100)} (
                    <Money value={latestApproval!.requestedDiscountTotal} />) — above the{' '}
                    {formatPct(capPct * 100)} cap.
                  </p>
                  {latestApproval!.reason && (
                    <p className="text-xs text-ink-3 mt-1 italic">“{latestApproval!.reason}”</p>
                  )}
                  {canSelfApprove ? (
                    <Link
                      to="/discount-approvals"
                      className="text-xs text-accent hover:underline mt-2 inline-block"
                    >
                      Review in discount approvals →
                    </Link>
                  ) : (
                    <p className="text-xs text-ink-3 mt-2">
                      Waiting for the owner to sign off before you can confirm.
                    </p>
                  )}
                </>
              ) : latestApproval?.status === 'APPROVED' ? (
                <>
                  <div className="text-sm font-medium text-positive">
                    Discount approved up to{' '}
                    {formatPct(
                      Number(latestApproval.approvedPct ?? latestApproval.requestedPct) * 100,
                    )}
                  </div>
                  <p className="text-xs text-ink-3 mt-1">
                    By {latestApproval.decidedBy?.name ?? 'owner'}
                    {latestApproval.decidedAt &&
                      ` · ${new Date(latestApproval.decidedAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}`}
                    .{' '}
                    {coveredByApproval
                      ? 'Next: set the delivery details below, then take the deposit to confirm.'
                      : 'The current discount is still above the approved ceiling — lower it to confirm.'}
                  </p>
                  {latestApproval.decisionNote && (
                    <p className="text-xs text-ink-3 mt-1 italic">
                      “{latestApproval.decisionNote}”
                    </p>
                  )}
                </>
              ) : latestApproval?.status === 'REJECTED' ? (
                <>
                  <div className="text-sm font-medium text-negative">
                    Discount rejected by {latestApproval.decidedBy?.name ?? 'owner'}
                  </div>
                  {latestApproval.decisionNote && (
                    <p className="text-sm text-ink-2 mt-1">{latestApproval.decisionNote}</p>
                  )}
                  <p className="text-xs text-ink-3 mt-2">
                    Lower the discount to ≤ {formatPct(capPct * 100)} to confirm, or request
                    approval again.
                  </p>
                  <Can permission="orders.create">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-2"
                      onClick={() => setReqOpen(true)}
                    >
                      Request approval again
                    </Button>
                  </Can>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium text-warning">
                    Discount {formatPct(discountPct * 100)} is above the {formatPct(capPct * 100)}{' '}
                    cap
                  </div>
                  <p className="text-xs text-ink-3 mt-1">
                    Owner approval is required before this order can be confirmed.
                  </p>
                  <Can permission="orders.create">
                    <Button size="sm" className="mt-2" onClick={() => setReqOpen(true)}>
                      Request owner approval
                    </Button>
                  </Can>
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Summary strip — customer, fulfilment, totals across the width so the
          page stays short instead of a tall right rail. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
        <Card title="Customer">
          <div className="text-ink font-medium">
            {o.customer.type === 'company'
              ? (o.customer.companyName ?? o.customer.name)
              : o.customer.name}
          </div>
          {o.customer.type === 'company' && (
            <div className="text-sm text-ink-3">
              Contact: {o.customer.contactPerson ?? o.customer.name}
            </div>
          )}
          <div className="text-sm text-ink-3">{o.customer.phone}</div>
          {o.customer.email && <div className="text-sm text-ink-3">{o.customer.email}</div>}
          {o.customer.type === 'company' && (o.customer.vatNumber || o.customer.brn) && (
            <div className="text-xs text-ink-3 kc-mono mt-1">
              {[
                o.customer.vatNumber ? `VAT ${o.customer.vatNumber}` : null,
                o.customer.brn ? `BRN ${o.customer.brn}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </Card>

        <Card title="Fulfilment">
          {fulfilmentPending ? (
            <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning">
              <span aria-hidden>⚠</span>
              <span>Delivery details not set yet — pickup/delivery wasn't chosen when the discount went for approval.</span>
            </div>
          ) : (
            <div className="text-sm text-ink">{o.fulfillmentType.replace(/_/g, ' ')}</div>
          )}
          {o.deliveryAddress && (
            <div className="text-sm text-ink-3 mt-2">
              <div className="text-xs uppercase tracking-wide">Address</div>
              {o.deliveryAddress}
            </div>
          )}
          {o.deliveryAddressPrecise && (
            <div className="text-sm text-ink mt-2">
              <div className="text-xs uppercase tracking-wide text-accent">Precise address</div>
              <div className="whitespace-pre-line">{o.deliveryAddressPrecise}</div>
            </div>
          )}
          {o.deliveryDate && (
            <div className="text-sm text-ink-3 mt-2">
              <div className="text-xs uppercase tracking-wide">Date</div>
              {formatBusinessDate(o.deliveryDate)}
            </div>
          )}
          {o.disposalQty > 0 && (
            <div className="mt-3 flex items-center gap-2 rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning">
              <span aria-hidden>♻️</span>
              <span>
                Collect <span className="font-semibold">{o.disposalQty}</span> old mattress
                {o.disposalQty === 1 ? '' : 'es'}
              </span>
            </div>
          )}
          {(o.status === 'CONFIRMED' || o.status === 'READY') &&
            o.fulfillmentType === 'delivery' && (
              <Can permission="orders.reschedule">
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => setEditDelivery(true)}
                >
                  Edit delivery date
                </Button>
              </Can>
            )}
          {/* DRAFT: set / change fulfilment before taking the deposit. The wizard
              defers this step for over-cap orders sent for owner approval, so the
              draft lands here as a pickup until the salesperson sets delivery.
              Stay open even once a sales_girl is otherwise locked out by payment —
              a pending fulfilment can't be left permanently unreachable. */}
          {o.status === 'DRAFT' && (!locked || fulfilmentPending) && (
            <Can permission="orders.create">
              <Button
                variant={fulfilmentPending ? 'primary' : 'secondary'}
                size="sm"
                className="mt-3"
                onClick={() => setEditFulfilment(true)}
              >
                {o.fulfillmentType === 'delivery' ? 'Edit delivery details' : 'Set delivery details'}
              </Button>
            </Can>
          )}
        </Card>

        <Card title="Totals">
          <dl className="space-y-2 text-sm">
            <Row label="Subtotal" value={<Money value={o.subtotal} />} />
            {o.discountTotal !== '0' && (
              <Row label="Discounts" value={<Money value={`-${o.discountTotal}`} />} />
            )}
            <Row label="VAT" value={<Money value={o.vatTotal} />} />
            {o.fastTrackFee !== '0' && Number(o.fastTrackFee) > 0 && (
              <Row label="Fast Track Delivery" value={<Money value={o.fastTrackFee} />} />
            )}
            <div className="border-t border-rule pt-2 mt-2">
              <Row
                label="Total"
                value={<Money value={o.total} className="text-accent font-medium" />}
              />
              <Row label="Paid" value={<Money value={o.paidTotal} />} />
              <Row
                label="Balance due"
                value={
                  <Money
                    value={o.balanceDue}
                    className={Number(o.balanceDue) > 0 ? 'text-negative' : 'text-positive'}
                  />
                }
              />
              {(() => {
                const credit = (o.invoice?.creditNoteApplications ?? []).reduce(
                  (s, a) => s + Number(a.amount),
                  0,
                );
                if (credit <= 0) return null;
                const netDue = Math.max(0, Number(o.balanceDue) - credit);
                return (
                  <div className="border-t border-rule-2 pt-2 mt-2">
                    <Row
                      label="Return credit applied"
                      value={<Money value={`-${credit}`} className="text-positive" />}
                    />
                    <Row
                      label={<span className="font-medium">Net to collect</span>}
                      value={<Money value={String(netDue)} className="font-medium text-ink" />}
                    />
                    <div className="text-[11px] text-ink-3 mt-1">
                      Credit from{' '}
                      {(o.invoice?.creditNoteApplications ?? [])
                        .map((a) => a.creditNote.number)
                        .join(', ')}
                      .
                    </div>
                  </div>
                );
              })()}
            </div>
          </dl>
        </Card>
      </div>

      <OrderNoteCard orderId={o.id} notes={o.notes} canEdit={perms.has('orders.create')} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Lines" bodyClassName="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium text-right">Qty</th>
                  <th className="px-3 py-2 font-medium text-right">Unit</th>
                  <th className="px-3 py-2 font-medium text-right">Discount</th>
                  <th className="px-6 py-2 font-medium text-right">Total (net)</th>
                </tr>
              </thead>
              <tbody>
                {o.lines.map((l) => (
                  <tr key={l.id} className="border-b border-rule-2">
                    <td className="px-6 py-3">
                      <div className="text-ink">
                        {l.productSnapshot?.name ?? l.product.name}
                        {l.isGift && (
                          <span className="ml-2 align-middle">
                            <StatusPill tone="accent">🎁 Free gift</StatusPill>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink-3 kc-mono">{l.productSnapshot?.sku ?? l.product.sku}</div>
                      {(l.selectedColor || l.selectedSideColor || l.selectedFabric) && (
                        <div className="text-[11px] text-ink-3 mt-0.5">
                          {[
                            l.selectedColor && `Colour: ${l.selectedColor}`,
                            l.selectedSideColor && `Side: ${l.selectedSideColor}`,
                            l.selectedFabric && `Fabric: ${l.selectedFabric}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                      <FirmnessBadge value={l.selectedDensity ?? l.product.density} className="mt-1" />
                      {l.product.category?.fulfillmentStrategy === 'MADE_TO_ORDER_FACTORY' && (
                        <div className="text-[11px] text-ink-3 mt-0.5">
                          Production: {lineProdDate(l) ?? '—'}
                          {o.status === 'CONFIRMED' && l.fulfillmentTask?.status !== 'READY' && (
                            <Can permission="orders.reschedule">
                              <Button
                                type="button"
                                variant="tertiary"
                                size="xs"
                                className="ml-2"
                                onClick={() =>
                                  setProdLine({
                                    id: l.id,
                                    label: `${l.qty}× ${l.productSnapshot?.name ?? l.product.name}`,
                                    date: lineProdDate(l) ?? todayIso,
                                  })
                                }
                              >
                                Reschedule
                              </Button>
                            </Can>
                          )}
                        </div>
                      )}
                      {l.deliveredQty >= l.qty ? (
                        <div className="mt-1">
                          <StatusPill tone="positive">Delivered</StatusPill>
                        </div>
                      ) : l.deliveredQty > 0 ? (
                        <div className="text-[11px] text-warning mt-0.5">{l.deliveredQty} of {l.qty} handed over</div>
                      ) : null}
                      {o.status === 'CONFIRMED' &&
                        !l.isGift &&
                        l.deliveredQty === 0 &&
                        l.fulfillmentTask?.status !== 'READY' &&
                        l.product.modelId && (
                          <Can permission="orders.change_size">
                            <Button
                              type="button"
                              variant="tertiary"
                              size="xs"
                              className="mt-1"
                              onClick={() =>
                                setChangeSizeLine({
                                  id: l.id,
                                  productId: l.product.id,
                                  modelId: l.product.modelId!,
                                  qty: l.qty,
                                  currentGross: Number(l.unitPrice) * l.qty - Number(l.discount),
                                  label: `${l.qty}× ${l.product.name}`,
                                })
                              }
                            >
                              Change size
                            </Button>
                          </Can>
                        )}
                    </td>
                    <td className="px-3 py-3 text-right kc-mono">{l.qty}</td>
                    <td className="px-3 py-3 text-right">
                      <Money value={l.unitPrice} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(l.discount) > 0 ? (
                        <div className="leading-tight">
                          <Money value={l.discount} />
                          <div className="text-[10px] text-ink-3">
                            {formatPct(
                              discountPctFromAmount(l.qty, Number(l.unitPrice), Number(l.discount)),
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-ink-4">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={l.lineTotal} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card title="Payments">
            {o.payments.length === 0 ? (
              <div className="text-sm text-ink-3">No payments recorded yet.</div>
            ) : (
              <ul className="space-y-2">
                {o.payments.map((p) => {
                  // Verification chip — only meaningful for non-cash payments.
                  const verifChip = p.rejectedAt ? (
                    <StatusPill tone="negative">rejected</StatusPill>
                  ) : p.verifiedAt ? (
                    <StatusPill tone="positive">verified</StatusPill>
                  ) : p.method !== 'cash' ? (
                    <StatusPill tone="warning">pending</StatusPill>
                  ) : null;
                  return (
                    <li
                      key={p.id}
                      className="flex items-center justify-between text-sm border-b border-rule-2 last:border-0 pb-2 last:pb-0 gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-ink">{p.method.replace('_', ' ')}</span>
                          {verifChip}
                        </div>
                        <div className="text-xs text-ink-3 mt-0.5">
                          {new Date(p.recordedAt).toLocaleString()} by {p.recordedBy.name}
                          {p.reference && ` · ref ${p.reference}`}
                        </div>
                        {p.rejectedAt && p.rejectionReason && (
                          <div className="text-xs text-negative mt-0.5">✕ {p.rejectionReason}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Money
                          value={p.amount}
                          className={p.voidedAt ? 'line-through text-ink-4' : ''}
                        />
                        {!p.voidedAt && (
                          <EmailDocButton
                            endpoint={`/payments/${p.id}/receipt/email`}
                            defaultEmail={o.customer.email}
                            title="Email receipt"
                            subtitle="Send the payment receipt to the customer."
                            buttonLabel="Email"
                            buttonSize="sm"
                          />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <Can permission="payments.record">
              {Number(o.balanceDue) > 0 && o.status !== 'CANCELLED' && (
                <RecordPaymentBox
                  orderId={o.id}
                  balanceDue={Number(o.balanceDue)}
                  shopName={o.shop.name}
                  userName={user?.name}
                />
              )}
            </Can>
          </Card>
        </div>

        <div className="space-y-4">
          {/* Lifecycle — read-only timeline (everyone) + actions (when allowed) */}
          <Card title="Lifecycle">
            <StatusTimeline order={o} />
            {!locked && (
              <div className="space-y-2 mt-4 pt-3 border-t border-rule-2">
                {o.status === 'DRAFT' && (
                  <Can permission="orders.confirm">
                    {!isUnpaid || o.paymentOverrideKind ? (
                      <>
                        <Button
                          className="w-full"
                          loading={transition.isPending}
                          disabled={confirmBlockedByDiscount}
                          title={
                            confirmBlockedByDiscount
                              ? 'This discount is above the cap — owner approval required first.'
                              : undefined
                          }
                          onClick={() => transition.mutate('confirm')}
                        >
                          Confirm order
                        </Button>
                        {confirmBlockedByDiscount && (
                          <p className="text-[11px] text-ink-3 text-center">
                            {approvalPending
                              ? 'Waiting on owner approval for the discount.'
                              : 'Request owner approval for the discount to confirm.'}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[11px] text-ink-3 text-center px-2">
                        Record the deposit in the Payments section — it confirms the order
                        automatically. An unpaid order can’t be confirmed.
                      </p>
                    )}
                  </Can>
                )}
                {o.status === 'CONFIRMED' && (
                  <Can permission="orders.confirm">
                    <Button
                      className="w-full"
                      loading={transition.isPending}
                      onClick={() => transition.mutate('ready')}
                    >
                      Mark ready
                    </Button>
                  </Can>
                )}
                {o.status === 'READY' && (
                  <Can permission="orders.confirm">
                    {Number(o.balanceDue) <= 0 || o.paymentOverrideKind ? (
                      <Button
                        className="w-full"
                        loading={transition.isPending}
                        onClick={() => transition.mutate('delivered')}
                      >
                        Mark delivered
                      </Button>
                    ) : (
                      <p className="text-[11px] text-ink-3 text-center px-2">
                        Record the remaining balance in the Payments section first — an order
                        can’t be delivered while money is still owing. Marking delivered
                        completes the order automatically once it’s paid in full.
                      </p>
                    )}
                  </Can>
                )}
                {/* Unpaid (draft or confirmed-but-unpaid): discard cleanly — no
                    invoice to reverse, just restock + mark CANCELLED. */}
                {isUnpaid &&
                  (o.status === 'DRAFT' || o.status === 'CONFIRMED' || o.status === 'READY') && (
                    <Can permission="orders.cancel">
                      <Button
                        variant="danger"
                        className="w-full"
                        onClick={() => setShowDiscard(true)}
                      >
                        Discard order
                      </Button>
                    </Can>
                  )}
                {/* Once money is in, cancelling reverses the issued invoice with a
                    credit note (deposit-handling choices) — not a plain discard. */}
                {!isUnpaid && (o.status === 'CONFIRMED' || o.status === 'READY') && (
                  <Can permission="orders.cancel">
                    <Button variant="danger" className="w-full" onClick={() => setShowCancel(true)}>
                      Cancel order
                    </Button>
                  </Can>
                )}
                {transition.isError && (
                  <div className="text-xs text-negative">
                    {getApiError(transition.error).message}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>

      {returnOpen && (
        <ReturnModal
          orderId={o.id}
          orderNumber={o.number}
          customerId={o.customer.id}
          receiptDate={o.deliveredAt ?? o.completedAt ?? o.confirmedAt ?? o.createdAt}
          lines={o.lines}
          onClose={() => setReturnOpen(false)}
        />
      )}

      {handoverOpen && (
        <HandoverLinesModal
          lines={o.lines}
          busy={handover.isPending}
          error={handover.isError ? getApiError(handover.error).message : null}
          onClose={() => setHandoverOpen(false)}
          onSubmit={(hl) => handover.mutate(hl)}
        />
      )}

      {changeSizeLine && (
        <ChangeSizeModal
          orderId={o.id}
          orderNumber={o.number}
          line={changeSizeLine}
          onClose={() => setChangeSizeLine(null)}
        />
      )}

      {editFulfilment && (
        <FulfilmentEditModal
          order={o}
          onClose={() => setEditFulfilment(false)}
          onSaved={() => {
            setEditFulfilment(false);
            qc.invalidateQueries({ queryKey: ['order', id] });
          }}
        />
      )}

      {editDelivery && (
        <DeliveryDateModal
          orderId={o.id}
          factoryLines={o.lines
            .filter(
              (l) =>
                !l.isGift && l.product.category?.fulfillmentStrategy === 'MADE_TO_ORDER_FACTORY',
            )
            .map((l) => ({ productId: l.product.id, qty: l.qty }))}
          initialDate={o.deliveryDate ? o.deliveryDate.slice(0, 10) : ''}
          initialRegion={(o.region as Region | null) ?? null}
          priority={Number(o.fastTrackFee) > 0}
          onClose={() => setEditDelivery(false)}
        />
      )}

      {prodLine && (
        <RescheduleModal
          title="Reschedule production"
          subtitle={`${o.number} · ${prodLine.label}`}
          initialDate={prodLine.date}
          minDate={todayIso}
          busy={reschedProd.isPending}
          error={reschedProd.isError ? getApiError(reschedProd.error).message : null}
          onClose={() => setProdLine(null)}
          onSubmit={({ date, override, reason }) =>
            reschedProd.mutate({ orderLineId: prodLine.id, date, override, reason })
          }
        />
      )}

      {overrideOpen && (
        <Modal
          onClose={() => setOverrideOpen(false)}
          title="Override payment verification"
          subtitle="Bypass the finance verification gate for this order"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setOverrideOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => applyOverride.mutate()}
                loading={applyOverride.isPending}
                disabled={!overrideReason.trim()}
              >
                Apply override
              </Button>
            </div>
          }
        >
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium mb-1.5">
                Kind
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setOverrideKind('PO')}
                  className={cn(
                    'px-3 py-2 rounded border text-sm transition text-left',
                    overrideKind === 'PO'
                      ? 'bg-accent text-white border-accent'
                      : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
                  )}
                >
                  <div className="font-medium">Purchase Order</div>
                  <div
                    className={cn(
                      'text-[11px] mt-0.5',
                      overrideKind === 'PO' ? 'text-paper-2/80' : 'text-ink-3',
                    )}
                  >
                    B2B customer on credit terms
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setOverrideKind('OWNER_CREDIT')}
                  className={cn(
                    'px-3 py-2 rounded border text-sm transition text-left',
                    overrideKind === 'OWNER_CREDIT'
                      ? 'bg-accent text-white border-accent'
                      : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
                  )}
                >
                  <div className="font-medium">Owner credit</div>
                  <div
                    className={cn(
                      'text-[11px] mt-0.5',
                      overrideKind === 'OWNER_CREDIT' ? 'text-paper-2/80' : 'text-ink-3',
                    )}
                  >
                    Ad-hoc trust — owner extends credit
                  </div>
                </button>
              </div>
            </div>
            <Input
              label="Reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder={
                overrideKind === 'PO'
                  ? 'PO #LBR-2026-018, 30-day terms'
                  : 'Regular customer — settling in cash next week'
              }
              autoFocus
            />
            <p className="text-xs text-ink-3">
              Applying the override clears any payment-rejected flag and lets the order move past
              CONFIRMED even if verified payments are below the deposit threshold. Logged in the
              audit trail with your name.
            </p>
          </div>
        </Modal>
      )}

      {showCancel && (
        <Modal
          onClose={() => setShowCancel(false)}
          title="Cancel this order"
          subtitle="Reverses issued invoices with credit notes. Choose how to handle the deposit and the goods."
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowCancel(false)}>
                Keep order
              </Button>
              <Button
                variant="danger"
                onClick={() => cancelMut.mutate()}
                loading={cancelMut.isPending}
                disabled={!cancelReason.trim()}
              >
                Cancel order
              </Button>
            </div>
          }
        >
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-3 font-medium mb-1.5">
                What kind of cancellation?
              </div>
              <div className="space-y-2">
                {[
                  {
                    mode: 'forfeit' as const,
                    title: 'Customer cancelled — keep the deposit',
                    desc: '50% deposit is non-refundable (T&C). Any paid balance is credit-noted.',
                    stock: 'Goods return to stock',
                  },
                  {
                    mode: 'refund' as const,
                    title: 'Refund in full — goods returned in good condition',
                    desc: 'Reverses everything, deposit included — goodwill or a manager-approved refund. Refund the cash from the credit note.',
                    stock: 'Goods return to stock',
                  },
                  {
                    mode: 'damaged' as const,
                    title: 'Refund in full — damaged goods (write off)',
                    desc: 'Company fault. Reverses everything, deposit included; refund the cash. The damaged units are written off.',
                    stock: 'Goods NOT restocked — written off',
                  },
                ].map((o) => (
                  <button
                    key={o.mode}
                    type="button"
                    onClick={() => setCancelMode(o.mode)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded border transition',
                      cancelMode === o.mode
                        ? 'bg-accent-soft border-accent'
                        : 'bg-paper-2 border-rule hover:border-accent/40',
                    )}
                  >
                    <div className="font-medium text-ink">{o.title}</div>
                    <div className="text-[11px] text-ink-3 mt-0.5">{o.desc}</div>
                    <div
                      className={cn(
                        'text-[10px] mt-1 font-medium uppercase tracking-wide',
                        o.mode === 'damaged' ? 'text-negative' : 'text-ink-3',
                      )}
                    >
                      {o.stock}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <Input
              label="Reason (required)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={
                cancelMode === 'damaged'
                  ? 'e.g. delivered mattress arrived torn'
                  : cancelMode === 'refund'
                    ? 'e.g. goodwill — VIP customer / manager approved'
                    : 'e.g. customer changed their mind'
              }
              autoFocus
            />
            {cancelMut.isError && (
              <div className="text-xs text-negative">{getApiError(cancelMut.error).message}</div>
            )}
          </div>
        </Modal>
      )}

      {showDiscard && (
        <Modal
          onClose={() => setShowDiscard(false)}
          title="Discard this order"
          subtitle={
            o.status === 'DRAFT'
              ? 'Nothing has been paid — the order will be marked CANCELLED.'
              : 'Nothing has been paid, so there’s no invoice to reverse. Any decremented/reserved stock returns to the warehouse and the order is marked CANCELLED.'
          }
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDiscard(false)}>
                Keep order
              </Button>
              <Button
                variant="danger"
                onClick={() => discardMut.mutate()}
                loading={discardMut.isPending}
              >
                Discard order
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm">
            <Input
              label="Reason (optional)"
              value={discardReason}
              onChange={(e) => setDiscardReason(e.target.value)}
              placeholder="e.g. customer changed their mind / created in error"
              autoFocus
            />
            {discardMut.isError && (
              <div className="text-xs text-negative">{getApiError(discardMut.error).message}</div>
            )}
          </div>
        </Modal>
      )}

      {reqOpen && (
        <Modal
          onClose={() => setReqOpen(false)}
          title="Request discount approval"
          subtitle={`${formatPct(discountPct * 100)} discount — above the ${formatPct(capPct * 100)} cap. The owner is notified to approve or reject.`}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setReqOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => requestApproval.mutate()} loading={requestApproval.isPending}>
                Send request
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-ink-3 font-medium">
                Reason for the owner (optional)
              </span>
              <textarea
                value={reqReason}
                onChange={(e) => setReqReason(e.target.value)}
                rows={3}
                placeholder="e.g. loyal customer, price-matched a competitor…"
                className="kc-input w-full mt-1"
                autoFocus
              />
            </label>
            {requestApproval.isError && (
              <div className="text-xs text-negative">
                {getApiError(requestApproval.error).message}
              </div>
            )}
          </div>
        </Modal>
      )}
    </PageScaffold>
  );
}

// Pick which lines (and qty) to hand over at the counter right now. The rest of
// the order stays a pro forma, delivered + invoiced later.
function HandoverLinesModal({
  lines,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  lines: OrderDetail['lines'];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (lines: Array<{ orderLineId: string; qty: number }>) => void;
}): JSX.Element {
  const pending = lines.filter((l) => l.deliveredQty < l.qty);
  const [qty, setQty] = useState<Record<string, number>>(
    Object.fromEntries(pending.map((l) => [l.id, l.qty - l.deliveredQty])),
  );
  const [pick, setPick] = useState<Record<string, boolean>>(
    Object.fromEntries(pending.map((l) => [l.id, true])),
  );
  const selected = pending
    .filter((l) => pick[l.id] && (qty[l.id] ?? 0) > 0)
    .map((l) => ({ orderLineId: l.id, qty: Math.min(qty[l.id]!, l.qty - l.deliveredQty) }));

  return (
    <Modal
      title="Hand over in store"
      subtitle="Records these items as handed over (creates a delivery note). No tax invoice yet — the order stays a pro forma until the whole order is delivered."
      onClose={onClose}
    >
      <div className="space-y-1">
        {pending.map((l) => {
          const remaining = l.qty - l.deliveredQty;
          return (
            <label key={l.id} className="flex items-center gap-3 text-sm border-b border-rule-2 py-2">
              <input
                type="checkbox"
                checked={!!pick[l.id]}
                onChange={(e) => setPick((p) => ({ ...p, [l.id]: e.target.checked }))}
              />
              <span className="flex-1">
                {l.product.name}
                <span className="text-ink-3 text-xs"> · {remaining} pending</span>
              </span>
              <input
                type="number"
                min={1}
                max={remaining}
                value={qty[l.id] ?? remaining}
                disabled={!pick[l.id]}
                onChange={(e) => setQty((q) => ({ ...q, [l.id]: Number(e.target.value) }))}
                className="kc-input w-16 kc-mono"
              />
            </label>
          );
        })}
      </div>
      {error && <div className="text-xs text-negative mt-3">{error}</div>}
      <div className="flex gap-2 mt-5">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          Cancel
        </Button>
        <Button className="flex-1" loading={busy} disabled={selected.length === 0} onClick={() => onSubmit(selected)}>
          Hand over {selected.length} item{selected.length === 1 ? '' : 's'}
        </Button>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between">
      <span className="text-ink-3">{label}</span>
      <span className="kc-mono kc-tnum text-ink">{value}</span>
    </div>
  );
}

// Read-only status progression — visible to everyone, including a sales girl
// who can no longer change a paid order's status.
function StatusTimeline({ order: o }: { order: OrderDetail }): JSX.Element {
  const fmt = (s: string | null): string | null =>
    s
      ? new Date(s).toLocaleString('en-MU', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;

  if (o.status === 'CANCELLED') {
    return (
      <ol>
        <TimelineRow label="Placed" at={fmt(o.createdAt)} state="done" by={o.salesperson?.name} />
        {o.confirmedAt && <TimelineRow label="Confirmed" at={fmt(o.confirmedAt)} state="done" by={o.confirmedBy?.name} />}
        <TimelineRow
          label="Cancelled"
          at={fmt(o.cancelledAt)}
          state="cancelled"
          by={o.cancelledBy?.name}
          note={o.cancellationReason ?? undefined}
          last
        />
      </ol>
    );
  }

  const steps: { key: string; label: string; at: string | null; by?: string }[] = [
    { key: 'DRAFT', label: 'Placed', at: o.createdAt, by: o.salesperson?.name },
    { key: 'CONFIRMED', label: 'Confirmed', at: o.confirmedAt, by: o.confirmedBy?.name },
    { key: 'READY', label: 'Ready', at: o.readyAt, by: o.readyBy?.name },
    ...(o.fulfillmentType === 'delivery'
      ? [{ key: 'DELIVERED', label: 'Delivered', at: o.deliveredAt, by: o.deliveredBy?.name }]
      : []),
    { key: 'COMPLETED', label: 'Completed', at: o.completedAt, by: o.completedBy?.name },
  ];
  let currentIdx = 0;
  steps.forEach((s, i) => {
    if (s.at) currentIdx = i;
  });

  return (
    <ol>
      {steps.map((s, i) => (
        <TimelineRow
          key={s.key}
          label={s.label}
          at={fmt(s.at)}
          state={s.at ? (i === currentIdx ? 'current' : 'done') : 'upcoming'}
          by={s.by}
          last={i === steps.length - 1}
        />
      ))}
    </ol>
  );
}

function TimelineRow({
  label,
  at,
  state,
  note,
  by,
  last,
}: {
  label: string;
  at: string | null;
  state: 'done' | 'current' | 'upcoming' | 'cancelled';
  note?: string;
  /** Who performed this step — shown under the timestamp when known. */
  by?: string;
  last?: boolean;
}): JSX.Element {
  const dot =
    state === 'cancelled'
      ? 'bg-negative border-negative'
      : state === 'current'
        ? 'bg-accent border-accent'
        : state === 'done'
          ? 'bg-positive border-positive'
          : 'bg-surface border-rule';
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className={cn('w-3 h-3 rounded-full border-2 shrink-0', dot)} />
        {!last && <span className="w-px flex-1 bg-rule-2 my-1" />}
      </div>
      <div className="flex-1 -mt-0.5 pb-2">
        <div
          className={cn(
            'text-sm',
            state === 'upcoming'
              ? 'text-ink-4'
              : state === 'cancelled'
                ? 'text-negative font-medium'
                : 'text-ink',
          )}
        >
          {label}
          {state === 'current' && (
            <span className="ml-2 text-[9px] uppercase tracking-wider text-accent">current</span>
          )}
        </div>
        <div className="text-[11px] text-ink-3">
          {at ?? (state === 'upcoming' ? 'pending' : '—')}
          {by && at && <span className="text-ink-4"> · by {by}</span>}
        </div>
        {note && <div className="text-[11px] text-ink-3 mt-0.5 italic">“{note}”</div>}
      </div>
    </li>
  );
}

// Every order carries a free-text internal note (captured at creation, shown &
// amendable here). Read-only for personas without orders.create.
function OrderNoteCard({
  orderId,
  notes,
  canEdit,
}: {
  orderId: string;
  notes: string | null;
  canEdit: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(notes ?? '');
  const save = useMutation({
    mutationFn: () => apiPatch(`/orders/${orderId}/notes`, { notes: val.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      setEditing(false);
      toast.show('Note saved.', 'success');
    },
    onError: (e) => toast.show(getApiError(e).message, 'error'),
  });
  return (
    <Card
      title="Note"
      action={
        canEdit && !editing ? (
          <Button
            variant="tertiary"
            size="xs"
            onClick={() => {
              setVal(notes ?? '');
              setEditing(true);
            }}
          >
            {notes ? 'Edit' : 'Add note'}
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            rows={3}
            maxLength={1000}
            autoFocus
            placeholder="Anything operations or finance should see…"
            className="kc-input w-full resize-y"
          />
          <div className="flex gap-2">
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : notes ? (
        <p className="text-sm text-ink whitespace-pre-line">{notes}</p>
      ) : (
        <p className="text-sm text-ink-3 italic">
          {canEdit ? 'No note yet — add one for operations or finance.' : 'No note.'}
        </p>
      )}
    </Card>
  );
}

function RecordPaymentBox({
  orderId,
  balanceDue,
  shopName,
  userName,
}: {
  orderId: string;
  balanceDue: number;
  shopName?: string;
  userName?: string;
}): JSX.Element {
  const { perms } = useAuth();
  const canRecordCheque = perms.has('payments.record_cheque');
  const [lines, setLines] = useState<PaymentDraft[]>(() => [emptyPayment(balanceDue.toFixed(2))]);
  const qc = useQueryClient();
  const record = useMutation({
    mutationFn: () =>
      apiPost(`/payments/orders/${orderId}/batch`, {
        payments: lines.map((p) => ({
          amount: Number(p.amount),
          method: p.method,
          reference: p.reference.trim() || null,
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      setLines([emptyPayment('')]);
    },
  });

  return (
    <div className="mt-4 pt-4 border-t border-rule">
      <div className="text-xs uppercase tracking-wide text-ink-3 mb-3">Record payment</div>
      <PaymentCapture
        lines={lines}
        onChange={setLines}
        total={balanceDue}
        userName={userName}
        shopName={shopName}
        canRecordCheque={canRecordCheque}
      />
      <Button
        className="mt-3 w-full"
        loading={record.isPending}
        disabled={!paymentsValid(lines, balanceDue)}
        onClick={() => record.mutate()}
      >
        Record payment
      </Button>
      {record.isError && (
        <div className="text-xs text-negative mt-2">{getApiError(record.error).message}</div>
      )}
    </div>
  );
}

// Earliest delivery lead (days from today) by fulfilment strategy — mirrors the
// backend + the New Order wizard. Imported/accessories next day, local 3, base 10.
const FULFILMENT_LEAD_BY_STRATEGY: Record<string, number> = {
  STOCK_AT_LOCATION: 1,
  MADE_TO_ORDER_FACTORY: 3,
  MADE_TO_ORDER_SUPPLIER: 10,
};

// Set / change a DRAFT order's fulfilment from the order page. Over-cap orders
// are sent for owner approval with only items + customer (the wizard defers the
// fulfilment step), so the draft starts as a pickup; after the owner approves,
// the salesperson captures the delivery details HERE, then takes the deposit.
// Mirrors the wizard's step-3 chooser, scoped to one already-created order.
function FulfilmentEditModal({
  order,
  onClose,
  onSaved,
}: {
  order: OrderDetail;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const shopsQuery = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<Array<{ id: string; code: string; name: string }>>('/shops'),
  });
  const shops = shopsQuery.data ?? [];

  const activeLines = order.lines.filter((l) => !l.isGift);
  const hasBackorder = activeLines.some((l) => l.backorder);
  const hasDeliveryRequired = activeLines.some((l) => l.product.category?.deliveryRequired);
  const factoryLines = activeLines
    .filter((l) => l.product.category?.fulfillmentStrategy === 'MADE_TO_ORDER_FACTORY')
    .map((l) => ({ productId: l.product.id, qty: l.qty }));

  const [type, setType] = useState<FulfillmentType>(
    order.fulfillmentType === 'delivery' || hasDeliveryRequired ? 'delivery' : 'pickup',
  );
  const [pickupShopId, setPickupShopId] = useState<string>(order.pickupShopId ?? order.shop.id);
  const [deliveryAddress, setDeliveryAddress] = useState(order.deliveryAddress ?? '');
  const [deliveryDate, setDeliveryDate] = useState(
    order.deliveryDate ? order.deliveryDate.slice(0, 10) : '',
  );
  const [region, setRegion] = useState<Region | null>(
    (order.region as Region | null) ?? (order.customer.region as Region | null) ?? null,
  );
  const [disposalQty, setDisposalQty] = useState(order.disposalQty);

  // Earliest selectable day = later of the goods' static lead and the live
  // factory headroom (excluding this order's own plan, which is re-made on save).
  const earliestQuery = useQuery({
    queryKey: ['earliest-delivery', order.id, factoryLines],
    queryFn: () =>
      apiPost<{ earliest: string | null }>('/scheduling/earliest-delivery', {
        lines: factoryLines,
        excludeOrderId: order.id,
      }),
    enabled: factoryLines.length > 0,
  });
  const earliestLeadDays = activeLines.reduce(
    (m, l) => Math.max(m, FULFILMENT_LEAD_BY_STRATEGY[l.product.category?.fulfillmentStrategy ?? 'STOCK_AT_LOCATION'] ?? 1),
    1,
  );
  // Fast Track makes delivery selectable from the next day — the static lead and
  // the factory-capacity floor are bypassed (the backend forces production through).
  const fastTrack = Number(order.fastTrackFee) > 0;
  const staticEarliest = addDaysIso(businessTodayIso(), fastTrack ? 1 : Math.max(1, earliestLeadDays));
  const capacityEarliest = earliestQuery.data?.earliest ?? null;
  const earliestDate =
    fastTrack || !capacityEarliest || capacityEarliest <= staticEarliest
      ? staticEarliest
      : capacityEarliest;

  const save = useMutation({
    mutationFn: () =>
      apiPatch(`/orders/${order.id}/fulfilment`, {
        fulfillmentType: type,
        pickupShopId: type === 'pickup' ? pickupShopId : null,
        deliveryAddress: type === 'delivery' ? deliveryAddress.trim() : null,
        // Precise address comes from the customer record — the server re-snapshots it.
        // Backorder orders defer the date (auto-scheduled when stock lands).
        deliveryDate: type === 'delivery' && !hasBackorder && deliveryDate ? deliveryDate : null,
        region: type === 'delivery' ? region ?? undefined : undefined,
        disposalQty: type === 'delivery' ? disposalQty : 0,
      }),
    onSuccess: onSaved,
  });

  const valid =
    type === 'pickup'
      ? !hasDeliveryRequired && !!pickupShopId
      : !!deliveryAddress.trim() &&
        !!region &&
        (hasBackorder || (!!deliveryDate && deliveryDate >= earliestDate));

  return (
    <Modal
      title="Delivery details"
      subtitle="Set how this order is fulfilled before taking the deposit."
      size="xl"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
            Save details
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            disabled={hasDeliveryRequired}
            onClick={() => !hasDeliveryRequired && setType('pickup')}
            className={cn(
              'p-4 rounded-lg border-2 transition text-left flex flex-col gap-1',
              hasDeliveryRequired
                ? 'bg-paper-2/50 border-rule opacity-50 cursor-not-allowed'
                : type === 'pickup'
                  ? 'bg-accent-soft border-accent'
                  : 'bg-paper-2 border-rule hover:border-accent/40',
            )}
          >
            <span className="text-2xl">🝬</span>
            <span className={cn('text-sm font-medium', type === 'pickup' && !hasDeliveryRequired ? 'text-accent' : 'text-ink')}>
              Pickup at shop
            </span>
            <span className="text-xs text-ink-3">
              {hasDeliveryRequired ? 'This order must be delivered' : 'Customer collects in store'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setType('delivery')}
            className={cn(
              'p-4 rounded-lg border-2 transition text-left flex flex-col gap-1',
              type === 'delivery' ? 'bg-accent-soft border-accent' : 'bg-paper-2 border-rule hover:border-accent/40',
            )}
          >
            <span className="text-2xl">🚚</span>
            <span className={cn('text-sm font-medium', type === 'delivery' ? 'text-accent' : 'text-ink')}>
              Delivery
            </span>
            <span className="text-xs text-ink-3">Ship to customer address</span>
          </button>
        </div>

        {type === 'pickup' && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Pickup shop</span>
            <select
              value={pickupShopId}
              onChange={(e) => setPickupShopId(e.target.value)}
              className="kc-input"
            >
              {shops.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                  {s.id === order.shop.id ? ' (selling shop)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {type === 'delivery' && (
          <div className="space-y-3">
            <AddressAutocomplete
              label="Delivery address"
              placeholder="Search the map — street, town, or nearest point"
              value={deliveryAddress}
              onChange={setDeliveryAddress}
            />
            {/* Precise address lives on the customer record — shown read-only. */}
            {order.deliveryAddressPrecise && (
              <div className="rounded-md border border-dashed border-rule p-3 text-xs">
                <div className="font-medium uppercase tracking-wide text-ink-3">Precise address · from customer</div>
                <div className="whitespace-pre-line text-ink mt-1">{order.deliveryAddressPrecise}</div>
                <div className="text-ink-4 mt-1">Edit it on the customer record.</div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1.5">
                Delivery date{hasBackorder && <span className="text-ink-4"> · optional</span>}
              </label>
              {hasBackorder && (
                <div className="mb-2 flex items-start gap-2 text-sm bg-info-soft text-info border border-info/30 rounded px-3 py-2.5">
                  <span className="text-base mt-px">📦</span>
                  <div>
                    <div className="font-medium">Delivery auto-scheduled when the stock arrives</div>
                    <div className="text-xs opacity-90 mt-0.5">
                      This order reserves a backordered item — the date is set automatically once the
                      container is received. Leave it blank, or pick a target date.
                    </div>
                  </div>
                </div>
              )}
              <DeliveryDatePicker
                region={region}
                onRegionChange={setRegion}
                value={deliveryDate}
                onChange={setDeliveryDate}
                earliestDate={earliestDate}
                priority={Number(order.fastTrackFee) > 0}
                customerNeedsRegion={!order.customer.region}
                customerLabel={
                  order.customer.type === 'company'
                    ? (order.customer.companyName ?? order.customer.name)
                    : order.customer.name
                }
              />
            </div>
            <div className="border border-dashed border-rule rounded-md p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-ink">Old mattress collection</div>
                  <div className="text-xs text-ink-3 mt-0.5">Free service · we'll dispose of them. How many?</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setDisposalQty(Math.max(0, disposalQty - 1))}
                    disabled={disposalQty === 0}
                    className="w-7 h-7 rounded border border-rule text-ink-2 hover:bg-paper-2 hover:text-ink transition"
                    aria-label="Decrease"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={disposalQty}
                    onChange={(e) => setDisposalQty(Math.max(0, Math.min(20, Number(e.target.value || 0))))}
                    className="w-14 text-center kc-input"
                  />
                  <button
                    type="button"
                    onClick={() => setDisposalQty(Math.min(20, disposalQty + 1))}
                    className="w-7 h-7 rounded border border-rule text-ink-2 hover:bg-paper-2 hover:text-ink transition"
                    aria-label="Increase"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {save.isError && (
          <div className="rounded border border-warning/40 bg-warning-soft/50 text-ink-2 text-xs px-3 py-2">
            {getApiError(save.error).message}
          </div>
        )}
      </div>
    </Modal>
  );
}

// Reschedule an order's delivery day. Uses the region-aware DeliveryDatePicker
// for normal picks; toggling "override" reveals a plain date field so a day the
// picker would block (off-route / full / too-soon) can be forced with a reason.
function DeliveryDateModal({
  orderId,
  factoryLines,
  initialDate,
  initialRegion,
  priority,
  onClose,
}: {
  orderId: string;
  factoryLines: Array<{ productId: string; qty: number }>;
  initialDate: string;
  initialRegion: Region | null;
  priority?: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [region, setRegion] = useState<Region | null>(initialRegion);
  const [date, setDate] = useState(initialDate);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const todayIso = businessTodayIso();

  // Soonest date the factory can finish this order — excludes the order's own
  // plan (it's dropped and re-made on reschedule) so it isn't counted twice.
  const earliestQuery = useQuery({
    queryKey: ['earliest-delivery', orderId, factoryLines],
    queryFn: () =>
      apiPost<{ earliest: string | null }>('/scheduling/earliest-delivery', {
        lines: factoryLines,
        excludeOrderId: orderId,
      }),
    enabled: factoryLines.length > 0,
  });
  // Fast Track bypasses the factory-capacity floor — the picker floors at tomorrow.
  const earliestDate = priority ? undefined : earliestQuery.data?.earliest ?? undefined;

  const mut = useMutation({
    mutationFn: () =>
      apiPatch(`/orders/${orderId}/delivery-date`, {
        deliveryDate: date,
        region: region ?? undefined,
        override,
        reason: override ? reason : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      onClose();
    },
  });

  const reasonMissing = override && !reason.trim();

  return (
    <Modal
      title="Reschedule delivery"
      subtitle="Pick a new delivery day"
      size="xl"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mut.mutate()}
            loading={mut.isPending}
            disabled={!date || reasonMissing}
          >
            Reschedule
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {override ? (
          <Input
            label="New delivery date"
            type="date"
            value={date}
            min={todayIso}
            onChange={(e) => setDate(e.target.value)}
          />
        ) : (
          <DeliveryDatePicker
            region={region}
            onRegionChange={setRegion}
            value={date}
            onChange={setDate}
            earliestDate={earliestDate}
            priority={priority}
          />
        )}

        {mut.isError && (
          <div className="rounded border border-warning/40 bg-warning-soft/50 text-ink-2 text-xs px-3 py-2">
            {getApiError(mut.error).message}
          </div>
        )}

        <label className="flex items-start gap-2 cursor-pointer select-none text-sm">
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => setOverride(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="text-ink">Override scheduling rules</span>
            <span className="block text-[11px] text-ink-3">
              Force a day the calendar blocks (off-route, full, or too soon). Logged with your name.
            </span>
          </span>
        </label>

        {override && (
          <Input
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reasonMissing ? 'A reason is required to override.' : undefined}
            placeholder="Customer requested earlier delivery"
          />
        )}
      </div>
    </Modal>
  );
}
